import {gql, useQuery} from '@apollo/client';
import {useEffect} from 'react';
import {LoadingSpinner} from 'common';
import {ReportModal} from '../form-components/ReportModal';

export const EXPORT_REQUEST = gql`
    query exportRequestQuery($guid: String!) {
        exportRequest(guid: $guid) {
            status
            errorMessage
            downloadUrl
        }
    }
`;

type ExportRequestInfo = {
    status: string;
    errorMessage: string | null;
    downloadUrl: string | null;
};

type ExportRequestModalProps = {
    /** Guid returned by the export mutation; the modal polls and is shown while this is non-null. */
    guid: string | null;
    onClose: () => void;
};

const POLL_INTERVAL_MS = 1500;

// Triggers the download in place via a hidden link rather than window.open(): the export can take far
// longer to generate than the click's popup-blocker grace period, so opening a new tab this late would
// get blocked. Navigating via a link click never does, and the presigned URL's Content-Disposition:
// attachment header makes the browser save the file instead of actually navigating away.
const triggerDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Generic export progress modal (BHPNPP-3084). Polls exportRequest(guid) until a downloadUrl or
 * errorMessage comes back: on success it downloads the presigned downloadUrl and closes itself; on
 * failure it shows errorMessage in place. Reused across any grid whose export mutation returns
 * {guid, status} — see useExportData.
 */
export const ExportRequestModal = ({guid, onClose}: ExportRequestModalProps) => {
    const {data, error, stopPolling} = useQuery<{ exportRequest: ExportRequestInfo }, { guid: string }>(
        EXPORT_REQUEST, {
            variables: {guid: guid ?? ''},
            skip: !guid,
            pollInterval: POLL_INTERVAL_MS,
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
        }
    );

    const downloadUrl = data?.exportRequest?.downloadUrl;
    const errorMessage = data?.exportRequest?.errorMessage;

    useEffect(() => {
        if (downloadUrl) {
            stopPolling();
            triggerDownload(downloadUrl);
            onClose();
        }
    }, [downloadUrl, stopPolling, onClose]);

    useEffect(() => {
        if (errorMessage) {
            stopPolling();
        }
    }, [errorMessage, stopPolling]);

    const failureMessage = errorMessage ?? (error ? 'Unable to generate the export. Please try again.' : null);

    return (
        <ReportModal visible={guid !== null} title="Export" onClose={onClose} maxWidth="max-w-md"
                     closeDisabled={!failureMessage}>
            <div className="flex flex-col items-center w-full p-6 gap-3">
                {failureMessage ? (
                    <div className="text-red-700 text-sm text-center">{failureMessage}</div>
                ) : (
                    <>
                        <LoadingSpinner text=""/>
                        <div className="text-sm text-center">
                            Preparing your export - this can take a moment. Please keep this window open.
                        </div>
                    </>
                )}
            </div>
        </ReportModal>
    );
};
